import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import { domHooks } from '@nxte-nl/dom-hooks';

import { ACCOUNT_NAVIGATION_LIST } from '../../constants/account';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { Link, LinkIntent, LinkType } from '../Link/Link';
import { Typography } from '../Typography/Typography';
import { AccountIcon } from '../icons';
import styles from './AccountNavigation.module.css';

const messages = defineMessages({
  logout: {
    id: 'src.components.AccountNavigation.logout',
    defaultMessage: 'Log out',
  },
  accountLabelText: {
    id: 'src.components.AccountNavigation.accountLabelText',
    defaultMessage: 'Account',
  },
});

export type Props = {
  isAccountLink: boolean;
  isHeaderV2?: boolean;
  isNavigationIconTextOn?: boolean;
  isOpened: boolean;
  isUserLoggedIn: boolean;
  logoutLoading: boolean;
  onClose: () => void;
  onLogoutUser: () => void;
  onToggle: () => void;
  pageLoading?: boolean;
};

export const AccountNavigation = React.memo((props: Props) => {
  const {
    pageLoading,
    logoutLoading,
    isUserLoggedIn,
    isAccountLink,
    isOpened,
    isHeaderV2,
    onToggle,
    onClose,
    onLogoutUser,
    isNavigationIconTextOn,
  } = props;

  const pathname = routerHooks.usePathName();
  const navigationRef = React.useRef<HTMLDivElement | null>(null);

  const accountIdentifyRedirect = navigationHooks.useAccountIdentifyRedirect();

  domHooks.useClickOutside(navigationRef, onClose, true);

  const shouldRenderDropdown = isUserLoggedIn && !pageLoading && isOpened;

  const {
    handleRedirectWithInnerParams,
  } = navigationHooks.useRedirectHandlerWithInnerParams();

  return (
    <nav className={styles.navigation} ref={navigationRef}>
      <Link
        type={isAccountLink ? LinkType.Href : LinkType.Span}
        to={accountIdentifyRedirect}
        withoutHoverStyles
        className={styles.link}
        {...isUserLoggedIn && { onClick: onToggle }}
      >
        <>
          <AccountIcon color={isHeaderV2 ? 'var(--header-text-color-v2)' : 'var(--header-text-color)'} />
          {isNavigationIconTextOn
        && <Typography variant='subText1' className={classNames(styles.iconsLabels, {
          [styles.headerV2IconLabels]: isHeaderV2,
        })}>
          <FormattedMessage {...messages.accountLabelText} />
        </Typography>}
        </>
      </Link>
      {shouldRenderDropdown && (
        <div className={styles.dropdown}>
          {ACCOUNT_NAVIGATION_LIST.map(({ title, route }) => {
            const redirect = handleRedirectWithInnerParams({ url: route });
            const isActiveLink = redirect === pathname;

            return (
              <Link
                key={redirect}
                type={isActiveLink ? LinkType.Span : LinkType.Href}
                withoutHoverStyles={isActiveLink}
                intent={LinkIntent.secondary}
                to={redirect}
                className={styles.navigationLink}
                {...!isActiveLink && { onClick: onToggle }}
              >
                <Typography
                  className={classNames({ [styles.textActive]: isActiveLink })}
                  variant='body2'
                >
                  <FormattedMessage {...title} />
                </Typography>
              </Link>
            );
          })}
          <Link
            type={LinkType.Span}
            className={classNames(styles.link, { [styles.linkDisabled]: logoutLoading })}
            withoutHoverStyles={logoutLoading}
            {...!logoutLoading && { onClick: onLogoutUser }}
          >
            <Typography
              className={styles.textActive}
              variant='body2'
            >
              <FormattedMessage {...messages.logout} />
            </Typography>
          </Link>
        </div>
      )}
    </nav>
  );
});

AccountNavigation.displayName = 'AccountNavigation';
